/**
 * The tenant Passes page on a phone (SAM-944, canvas PhonePasses): «Passes» with «New pass», a search box, ONE
 * dropdown for range and status instead of Date range / Filters, and a card per pass with the pager under the
 * list. Presentational: Invitations.tsx owns the query, the filters and the detail drawer. Replaces PassesPhoneList.
 */
import React from 'react';
import { PhoneListState, PhonePager } from '../../components/phone/PhoneCards';
import { PhoneRowList, PhoneSearch, PhoneSelect, PhoneTitleRow, type PhoneOptionGroup, type PhoneRow } from '../../components/phone/PhoneListParts';
import { useT } from '../../i18n/admin';

export type PhonePassCard = PhoneRow;

interface Props {
  items: PhonePassCard[];
  loading: boolean;
  page: number;
  limit: number;
  total: number;
  onPage: (page: number) => void;
  query: string;
  onQuery: (q: string) => void;
  /** Id of the chosen option of the dropdown. */
  filter: string;
  groups: PhoneOptionGroup[];
  onFilter: (id: string) => void;
  onNew: () => void;
}

export const PassesPhone: React.FC<Props> = ({ items, loading, page, limit, total, onPage, query, onQuery, filter, groups, onFilter, onNew }) => {
  const { t } = useT();
  const chosen = groups.flatMap((g) => g.options).find((o) => o.id === filter);
  // «Today · 4»: the chosen range or status and how many passes it holds.
  const display = chosen ? (loading ? chosen.label : `${chosen.label} · ${total.toLocaleString('en-US')}`) : '';
  return (
    <div className="flex min-w-0 flex-col gap-3 bg-[var(--surface-2,#f5f5f5)] p-4 pb-8">
      <PhoneTitleRow title={t('phonePasses.title')} onNew={onNew} />
      <PhoneSearch placeholder={t('phonePasses.search')} value={query} onChange={onQuery} />
      <PhoneSelect ariaLabel={t('phonePasses.filterLabel')} value={filter} display={display} groups={groups} onChange={onFilter} />
      <PhoneListState loading={loading} failed={false} empty={!loading && items.length === 0} emptyText={t('phonePasses.empty')} />
      {!loading && items.length > 0 && <PhoneRowList rows={items} />}
      {!loading && <PhonePager page={page} limit={limit} total={total} onPage={onPage} />}
    </div>
  );
};
